import axios from 'axios';
import { logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_BFF_URL || '';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  if (import.meta.env.VITE_MOCK_AUTH === 'true') {
    config.headers.Authorization = 'Bearer valid-token';
  } else {
    try {
      const { getFirebaseAuth } = await import('../lib/firebase');
      const { getIdToken } = await import('firebase/auth');
      const auth = getFirebaseAuth();
      if (auth.currentUser) {
        const token = await getIdToken(auth.currentUser);
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Firebase not initialized or user not logged in
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response) {
      const { status, data, headers } = error.response;
      logger.error(`API Error [${status}]`, error, {
        url: originalRequest?.url,
        message: data?.error?.message
      });

      if (data?.error?.code === 'AUTH_006') {
        window.location.href = '/login?message=concurrent_logout';
      } else if (status === 401 && import.meta.env.VITE_MOCK_AUTH !== 'true') {
        window.location.href = '/login';
      }

      // Handle 429 Rate Limiting
      if (status === 429 && originalRequest) {
        // Skip retrying dashboard init to avoid lockup
        if (originalRequest.url && originalRequest.url.includes('/dashboard/init')) {
          return Promise.reject(error);
        }

        originalRequest._retryCount = originalRequest._retryCount || 0;
        const maxRetries = 3;

        if (originalRequest._retryCount < maxRetries) {
          originalRequest._retryCount++;
          
          // Get delay from Retry-After header, or fallback to exponential backoff
          const retryAfterHeader = headers?.['retry-after'] || headers?.['Retry-After'];
          let delayMs = 1000;
          if (retryAfterHeader) {
            const seconds = parseInt(retryAfterHeader, 10);
            if (!isNaN(seconds)) delayMs = seconds * 1000;
          } else {
            delayMs = Math.pow(2, originalRequest._retryCount) * 1000 + Math.random() * 100;
          }

          // Trigger custom event to show warning toast
          const retrySeconds = Math.ceil(delayMs / 1000);
          window.dispatchEvent(new CustomEvent('api-rate-limited', { detail: { retryAfter: retrySeconds } }));

          // Wait and retry the request
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          return apiClient(originalRequest);
        }
      }
    } else {
      logger.error('Network/Server Error', error);
    }
    return Promise.reject(error);
  }
);
